import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'select';
  showLabel?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'select',
  showLabel = true 
}) => {
  const { language, changeLanguage, availableLanguages } = useLanguage();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Ensure we have a valid language, default to 'en' if not
  const currentLanguage = language || 'en';

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (variant === 'dropdown') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button 
          className={`flex items-center px-2 py-1.5 rounded-md ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          aria-label={t('common.changeLanguage', 'Change language')}
          onClick={() => setIsOpen(!isOpen)}
        >
          <Globe className={`h-4 w-4 mr-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
          <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
            {t(`languages.${currentLanguage}`, 
              currentLanguage === 'my' ? 'မြန်မာ' : 
              currentLanguage === 'th' ? 'ไทย' : 'English'
            )}
          </span>
        </button>
        {isOpen && (
          <div className={`absolute right-0 mt-1 w-32 rounded-md shadow-lg py-1 z-50 ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  changeLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`block w-full text-left px-4 py-2 text-sm ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} ${currentLanguage === lang.code ? (theme === 'dark' ? 'bg-gray-700 text-blue-400' : 'bg-gray-100 text-blue-600') : (theme === 'dark' ? 'text-gray-200' : 'text-gray-700')}`}
              >
                {t(`languages.${lang.code}`, 
                  lang.code === 'my' ? 'မြန်မာ' : 
                  lang.code === 'th' ? 'ไทย' : 'English'
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      {showLabel && (
        <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          {t('common.language', 'Language')}:
        </span>
      )}
      <select
        value={currentLanguage}
        onChange={(e) => changeLanguage(e.target.value)}
        className={`block w-24 py-1 px-2 text-sm rounded-md ${
          theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
            : 'bg-white border-gray-300 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500'
        }`}
        aria-label={t('common.changeLanguage', 'Change language')}
      >
        {availableLanguages.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {t(`languages.${lang.code}`, 
              lang.code === 'my' ? 'မြန်မာ' : 
              lang.code === 'th' ? 'ไทย' : 'English'
            )}
          </option>
        ))}
      </select>
    </div>
  );
};

export default LanguageSwitcher;
